import { useState, useEffect, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import { SubscriptionProvider } from '@/context/SubscriptionContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { SubscriptionModals } from '@/components/SubscriptionModals';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { SkipLink, ScrollToTop } from '@/components/Accessibility';
import AuthCallback from '@/components/AuthCallback';
import { AmberAlertBanner } from '@/components/AmberAlertBanner';
import SEOProvider from '@/components/SEOProvider';
import { WorkspaceSidebar } from '@/components/WorkspaceSidebar';

// ─── Stale-chunk auto-recovery ────────────────────────────────────────
// When the dev server recompiles (or production redeploys) the webpack
// build, the chunk filenames change. Long-lived browser tabs still
// reference the OLD filenames inside their already-loaded JS, so the
// next time the user navigates to a lazy-loaded route, the dynamic
// `import()` resolves to a 404 / hang and React Suspense gets stuck on
// the "Loading..." spinner forever — the canonical "spinning and
// timing out" failure mode.
//
// This handler catches the `ChunkLoadError` / dynamic-import failure
// at the window level, sets a sentinel in sessionStorage so we don't
// reload-loop, and forces a hard reload that picks up the fresh
// chunk manifest. Idempotent — only attaches once per page load.
// 2026-05-17 — added after Casey hit infinite spinner on /admin/outreach
// post-redeploy.
if (typeof window !== 'undefined' && !window.__chunkErrorHandlerInstalled) {
  window.__chunkErrorHandlerInstalled = true;
  const reloadOnce = (label) => {
    if (sessionStorage.getItem('__chunkReloadAttempted') === '1') {
      // Already tried — don't loop. Surface a console warning so
      // DevTools shows a clear cause.
      console.error(`Stale chunk persisted after reload (${label}). `
        + 'Hard refresh manually (Cmd/Ctrl+Shift+R) or clear cache.');
      return;
    }
    sessionStorage.setItem('__chunkReloadAttempted', '1');
    console.warn(`Stale webpack chunk detected (${label}); reloading.`);
    window.location.reload();
  };
  window.addEventListener('error', (event) => {
    const msg = String(event?.message || event?.error?.message || '');
    if (/ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module/i.test(msg)) {
      reloadOnce('error');
    }
  });
  window.addEventListener('unhandledrejection', (event) => {
    const msg = String(event?.reason?.message || event?.reason || '');
    if (/ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module/i.test(msg)) {
      reloadOnce('unhandledrejection');
    }
  });
  // Clear the sentinel on a clean navigation away (so subsequent
  // deploys can self-heal again).
  window.addEventListener('beforeunload', () => {
    if (sessionStorage.getItem('__chunkReloadAttempted') === '1') {
      // Keep the sentinel only for the auto-triggered reload — clear
      // on any other unload.
      const stillSpinning = !!document.querySelector('[role="status"]');
      if (!stillSpinning) sessionStorage.removeItem('__chunkReloadAttempted');
    }
  });
}
// ──────────────────────────────────────────────────────────────────────


// Core pages - loaded immediately
import HomePage from '@/pages/HomePage';
import BrowseCases from '@/pages/BrowseCases';
import CaseDetail from '@/pages/CaseDetail';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';

// Lazy-loaded pages for better initial load performance
const ReportCase = lazy(() => import('@/pages/ReportCase'));
const MapView = lazy(() => import('@/pages/MapView'));
const SubmitTip = lazy(() => import('@/pages/SubmitTip'));
const SubmitTipGeneral = lazy(() => import('@/pages/SubmitTipGeneral'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const PaymentSuccess = lazy(() => import('@/pages/PaymentSuccess'));
const ResourcesPage = lazy(() => import('@/pages/ResourcesPage'));
const AdminExternalCases = lazy(() => import('@/pages/AdminExternalCases'));
const AdminJobStatus = lazy(() => import('@/pages/AdminJobStatus'));
const AdminAddCase = lazy(() => import('@/pages/AdminAddCase'));
const AdminEditCase = lazy(() => import('@/pages/AdminEditCase'));
const UnclaimedPersons = lazy(() => import('@/pages/UnclaimedPersons'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const CompareCases = lazy(() => import('@/pages/CompareCases'));
const AddNamusCase = lazy(() => import('@/pages/AddNamusCase'));
const DuplicateManagement = lazy(() => import('@/pages/DuplicateManagement'));
const OrganizationVerification = lazy(() => import('@/pages/OrganizationVerification'));
const AdminOrganizations = lazy(() => import('@/pages/AdminOrganizations'));
const AdminCaseReview = lazy(() => import('@/pages/AdminCaseReview'));
const AdminOutreachQueue = lazy(() => import('@/pages/AdminOutreachQueue'));
const AdminManualSearch = lazy(() => import('@/pages/AdminManualSearch'));
const AdminPerpReviewBoard = lazy(() => import('@/pages/AdminPerpReviewBoard'));
const AdminPreDraftPreview = lazy(() => import('@/pages/AdminPreDraftPreview'));
const AnalyticsDashboard = lazy(() => import('@/pages/AnalyticsDashboard'));
const AlertPreferences = lazy(() => import('@/pages/AlertPreferences'));
const PotentialMatches = lazy(() => import('@/pages/PotentialMatches'));
const FacialSearchPage = lazy(() => import('@/pages/FacialSearchPage'));
const DonatePage = lazy(() => import('@/pages/DonatePage'));
const DonationSuccessPage = lazy(() => import('@/pages/DonationSuccessPage'));
const FamilyVerificationPage = lazy(() => import('@/pages/FamilyVerificationPage'));
const ResourcesHubPage = lazy(() => import('@/pages/ResourcesHubPage'));
const TermsOfServicePage = lazy(() => import('@/pages/TermsOfService'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicy'));
const DataSourcesPage = lazy(() => import('@/pages/DataSourcesPage'));
const DisclaimersPage = lazy(() => import('@/pages/Disclaimers'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const SuccessStoriesPage = lazy(() => import('@/pages/SuccessStoriesPage'));
const SubmitSuccessStoryPage = lazy(() => import('@/pages/SubmitSuccessStoryPage'));
const MediaKitPage = lazy(() => import('@/pages/MediaKitPage'));
const AmberAlertsPage = lazy(() => import('@/pages/AmberAlertsPage'));
const BlogPage = lazy(() => import('@/pages/BlogPage'));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage'));
const AdminBlogPage = lazy(() => import('@/pages/AdminBlogPage'));
const AdminSuggestionsReview = lazy(() => import('@/pages/AdminSuggestionsReview'));
const AdminMergeCases = lazy(() => import('@/pages/AdminMergeCases'));
const AdminRelatedCases = lazy(() => import('@/pages/AdminRelatedCases'));
const FacialMatchesPage = lazy(() => import('@/pages/FacialMatchesPage'));
const GenealogyPage = lazy(() => import('@/pages/GenealogyPage'));
const WorkspacePage = lazy(() => import('@/pages/WorkspacePage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ClusterDetection = lazy(() => import('@/pages/ClusterDetection'));
const AdministrativeLinksPage = lazy(() => import('@/pages/AdministrativeLinksPage')); // Now renamed to Record Reconciliation
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const AdminUsersPage = lazy(() => import('@/pages/AdminUsersPage'));
const AdminRemovedCases = lazy(() => import('@/pages/AdminRemovedCases'));
const AdminHVFPAlerts = lazy(() => import('@/pages/AdminHVFPAlerts'));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'));
const UnsubscribePage = lazy(() => import('@/pages/UnsubscribePage'));
const WhyPage = lazy(() => import('@/pages/WhyPage'));
const OrgDashboardPage = lazy(() => import('@/pages/OrgDashboardPage'));
const AdminFeedbackDashboard = lazy(() => import('@/pages/AdminFeedbackDashboard'));
const AdminEmailsPage = lazy(() => import('@/pages/AdminEmailsPage'));
const AdminContactSubmissions = lazy(() => import('@/pages/AdminContactSubmissions'));
const AdminDateFix = lazy(() => import('@/pages/AdminDateFix'));
const AdminReunifications = lazy(() => import('@/pages/AdminReunifications'));
const AdminIntelExtraction = lazy(() => import('@/pages/AdminIntelExtraction'));
const AdminNetworkGraph = lazy(() => import('@/pages/admin/AdminNetworkGraph'));
const AdminPodcastNeedsReview = lazy(() => import('@/pages/AdminPodcastNeedsReview'));
const AdminPartialRemainsReview = lazy(() => import('@/pages/AdminPartialRemainsReview'));
const AdminAnnotations = lazy(() => import('@/pages/AdminAnnotations'));
const AdminResolverAudit = lazy(() => import('@/pages/AdminResolverAudit'));
const AdminDumpSiteClusters = lazy(() => import('@/pages/AdminDumpSiteClusters'));
const AdminNeverScoredMP = lazy(() => import('@/pages/AdminNeverScoredMP'));
const AdminFluiddbDedup = lazy(() => import('@/pages/AdminFluiddbDedup'));
const AdminMaintenancePage = lazy(() => import('@/pages/AdminMaintenancePage'));
const WorkbenchFeed = lazy(() => import('@/pages/WorkbenchFeed'));
const AdminFlaggedMatches = lazy(() => import('@/pages/AdminFlaggedMatches'));
const AdminPerpetratorClusters = lazy(() => import('@/pages/AdminPerpetratorClusters'));
const ResolvedCases = lazy(() => import('@/pages/ResolvedCases'));
const InvestigationAssistant = lazy(() => import('@/pages/InvestigationAssistant'));
const SharedInvestigation = lazy(() => import('@/pages/SharedInvestigation'));
const MatchQualityDashboard = lazy(() => import('@/pages/MatchQualityDashboard'));
const ForensicBlindSpots = lazy(() => import('@/pages/ForensicBlindSpots'));
const ComparisonPage = lazy(() => import('@/pages/ComparisonPage'));
const OrganizationAccessPage = lazy(() => import('@/pages/OrganizationAccessPage'));
const MethodologyPage = lazy(() => import('@/pages/MethodologyPage'));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-label="Loading page">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-safety-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Router wrapper to handle auth callback detection
function AppRouter() {
  const location = useLocation();

  // Capture ?ref= / ?utm_source= once on mount and on every route change.
  // Stored in localStorage as 'referral_source' for attribution downstream.
  useEffect(() => {
    import('@/lib/referral').then(({ captureReferralFromUrl }) => {
      captureReferralFromUrl();
    }).catch(() => {});
  }, [location.search]);

  // Funnel telemetry: log a page_view on every route change.
  // Lazy-loaded so it never blocks the auth-callback fast path below.
  useEffect(() => {
    import('@/lib/telemetry').then(({ trackPageView }) => {
      trackPageView(location.pathname, { search: location.search || null });
    }).catch(() => {});
  }, [location.pathname, location.search]);
  
  // Check URL fragment for session_id SYNCHRONOUSLY during render (prevents race conditions)
  // This must happen BEFORE ProtectedRoute checks existing session_token
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <SkipLink />
      <ScrollToTop />
      <AmberAlertBanner />
      <Navbar />
      <WorkspaceSidebar />
      <main id="main-content" tabIndex="-1" className="outline-none pb-16 md:pb-0">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/browse" element={<BrowseCases />} />
            <Route path="/case/:id" element={<CaseDetail />} />
            <Route path="/report" element={<ReportCase />} />
            <Route path="/map" element={<MapView />} />
            <Route path="/tips" element={<SubmitTipGeneral />} />
            <Route path="/tip/:caseId" element={<SubmitTip />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/organization-access" element={<OrganizationAccessPage />} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route path="/research-integrity" element={<MethodologyPage />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/resources" element={<ResourcesHubPage />} />
            <Route path="/databases" element={<ResourcesPage />} />
            <Route path="/data-sources" element={<DataSourcesPage />} />
            <Route path="/admin/external" element={<AdminExternalCases />} />
            <Route path="/admin/jobs" element={<AdminJobStatus />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/maintenance" element={<AdminMaintenancePage />} />
            <Route path="/admin/match-quality" element={<MatchQualityDashboard />} />
            <Route path="/forensic-blind-spots" element={<ForensicBlindSpots />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/compare" element={<CompareCases />} />
            <Route path="/add-namus-case" element={<AddNamusCase />} />
            <Route path="/admin/duplicates" element={<DuplicateManagement />} />
            <Route path="/admin/merge-cases" element={<AdminMergeCases />} />
            <Route path="/admin/related-cases" element={<AdminRelatedCases />} />
            <Route path="/admin/facial-matches" element={<FacialMatchesPage />} />
            <Route path="/verify-organization" element={<OrganizationVerification />} />
            <Route path="/org/dashboard" element={<OrgDashboardPage />} />
            <Route path="/admin/organizations" element={<AdminOrganizations />} />
            <Route path="/admin/case-review" element={<AdminCaseReview />} />
            <Route path="/admin/outreach" element={<AdminOutreachQueue />} />
            <Route path="/admin/manual-search" element={<AdminManualSearch />} />
            <Route
              path="/admin/outreach/naso-review-board"
              element={<Navigate to="/admin/outreach/review-board/naso" replace />}
            />
            <Route
              path="/admin/outreach/review-board/:perpetrator_slug"
              element={<AdminPerpReviewBoard />}
            />
            <Route
              path="/admin/outreach/pre-draft-preview/:perpetrator_slug"
              element={<AdminPreDraftPreview />}
            />
            <Route
              path="/admin/outreach/pre-draft-preview/:perpetrator_slug/:case_id"
              element={<AdminPreDraftPreview />}
            />
            <Route path="/admin/podcast-needs-review" element={<AdminPodcastNeedsReview />} />
            <Route path="/admin/partial-remains-review" element={<AdminPartialRemainsReview />} />
            <Route path="/admin/annotations" element={<AdminAnnotations />} />
            <Route path="/admin/resolver-audit" element={<AdminResolverAudit />} />
            <Route path="/admin/dump-site-clusters" element={<AdminDumpSiteClusters />} />
            <Route path="/admin/never-scored-mp" element={<AdminNeverScoredMP />} />
            <Route path="/admin/fluiddb-dedup" element={<AdminFluiddbDedup />} />
            <Route path="/workbench" element={<WorkbenchFeed />} />
            <Route path="/admin/flagged-matches" element={<AdminFlaggedMatches />} />
            <Route path="/admin/perpetrator-clusters" element={<AdminPerpetratorClusters />} />
            <Route path="/resolved" element={<ResolvedCases />} />
            <Route path="/admin/add-case" element={<AdminAddCase />} />
            <Route path="/admin/edit-case/:caseId" element={<AdminEditCase />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/emails" element={<AdminEmailsPage />} />
            <Route path="/admin/contact-submissions" element={<AdminContactSubmissions />} />
            <Route path="/admin/removed-cases" element={<AdminRemovedCases />} />
            <Route path="/admin/hvfp-alerts" element={<AdminHVFPAlerts />} />
            <Route path="/unclaimed-persons" element={<UnclaimedPersons />} />
            <Route path="/analytics" element={<AnalyticsDashboard />} />
            <Route path="/alerts" element={<AlertPreferences />} />
            <Route path="/potential-matches" element={<PotentialMatches />} />
            <Route path="/comparison/:matchId" element={<ComparisonPage />} />
            <Route path="/administrative-links" element={<AdministrativeLinksPage />} />
            <Route path="/record-reconciliation" element={<AdministrativeLinksPage />} />
            <Route path="/clusters" element={<ClusterDetection />} />
            <Route path="/facial-search" element={<FacialSearchPage />} />
            <Route path="/facial-comparisons" element={<FacialMatchesPage />} />
            <Route path="/donate" element={<DonatePage />} />
            <Route path="/donation/success" element={<DonationSuccessPage />} />
            <Route path="/family-verification" element={<FamilyVerificationPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/disclaimers" element={<DisclaimersPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/impact-stories" element={<SuccessStoriesPage />} />
            {/* Legacy slug — preserved as a redirect to /impact-stories
                for external links and crawlers. */}
            <Route path="/success-stories" element={<Navigate to="/impact-stories" replace />} />
            <Route path="/submit-success-story" element={<SubmitSuccessStoryPage />} />
            <Route path="/media-kit" element={<MediaKitPage />} />
            <Route path="/amber-alerts" element={<AmberAlertsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/admin/blog" element={<AdminBlogPage />} />
            <Route path="/admin/suggestions" element={<AdminSuggestionsReview />} />
            <Route path="/admin/feedback" element={<AdminFeedbackDashboard />} />
            <Route path="/admin/date-fix" element={<AdminDateFix />} />
            <Route path="/admin/reunifications" element={<AdminReunifications />} />
            <Route path="/admin/intel" element={<AdminIntelExtraction />} />
            <Route path="/admin/network" element={<AdminNetworkGraph />} />
            <Route path="/admin/investigate" element={<InvestigationAssistant />} />
            <Route path="/genealogy" element={<GenealogyPage />} />
            <Route path="/workspace" element={<WorkspacePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/why" element={<WhyPage />} />
            <Route path="/shared/investigation/:shareId" element={<SharedInvestigation />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <MobileBottomNav />
    </>
  );
}

function App() {
  return (
    <SEOProvider>
      <ThemeProvider>
        <div className="min-h-screen bg-background font-body">
          <BrowserRouter>
            <AuthProvider>
              <SubscriptionProvider>
                <AppRouter />
                <SubscriptionModals />
                <Toaster position="top-right" />
              </SubscriptionProvider>
            </AuthProvider>
          </BrowserRouter>
        </div>
      </ThemeProvider>
    </SEOProvider>
  );
}

export default App;
